// Personal playlists, persisted by the shared backend (/me/playlists) so they follow the singer Web ↔ Mobile.
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

const localKey = (userId) => `ulc_playlists_${userId}`;
const sameList = (a, b) => a.name === b.name && a.song_ids.join(",") === b.song_ids.join(",");
const migrating = {};

// One-time import of lists created while they lived on this device. Lists already on the server
// (same name + same songs) are skipped, and the local copy is kept as a backup instead of deleted.
async function migrateLocal(userId) {
  let local = [];
  try { local = JSON.parse(localStorage.getItem(localKey(userId)) || "[]"); } catch (err) { console.error("Listas locais ilegíveis", err); }
  if (!local.length) return;
  const remote = await api("/me/playlists");
  for (const p of local) {
    const item = { name: (p.name || "Lista").trim() || "Lista", song_ids: p.song_ids || [] };
    if (!remote.some((r) => sameList(r, item))) await api("/me/playlists", { method: "POST", body: JSON.stringify(item) });
  }
  localStorage.setItem(`${localKey(userId)}_importadas`, JSON.stringify(local));
  localStorage.removeItem(localKey(userId));
}

async function fetchPlaylists(userId) {
  if (!migrating[userId]) migrating[userId] = migrateLocal(userId).finally(() => { delete migrating[userId]; });
  await migrating[userId];
  return api("/me/playlists");
}

export const usePlaylists = (userId) =>
  useQuery({ queryKey: ["my-playlists", userId], queryFn: () => fetchPlaylists(userId), enabled: !!userId });

export function usePlaylistMutations() {
  const qc = useQueryClient();
  const done = () => qc.invalidateQueries({ queryKey: ["my-playlists"] });
  const create = useMutation({ mutationFn: (p) => api("/me/playlists", { method: "POST", body: JSON.stringify(p) }), onSuccess: done });
  const update = useMutation({ mutationFn: ({ id, ...patch }) => api(`/me/playlists/${id}`, { method: "PUT", body: JSON.stringify(patch) }), onSuccess: done });
  const remove = useMutation({ mutationFn: (id) => api(`/me/playlists/${id}`, { method: "DELETE" }), onSuccess: done });
  return { create, update, remove };
}

/** Authenticated share link: carries only song ids; the recipient must sign in and audio stays streamed. */
export function shareUrl(playlist) {
  const q = new URLSearchParams({ n: playlist.name, s: playlist.song_ids.join(",") });
  return `${window.location.origin}/lista?${q.toString()}`;
}
